import { useLazyQuery } from '@apollo/client';
import type { FetchPolicy } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import type { CustomValuesDiscoveryData, CustomValuesDiscoveryQueryResponse } from '@internal-audit/domain/graphql-types';
import { CUSTOM_VALUES_DISCOVERY } from '@internal-audit/graphql/queries/customValuesDiscovery';
import { useCallback, useMemo } from 'react';

/** Default values returned before discovery data has been fetched. */
const DEFAULT_CUSTOM_VALUES: CustomValuesDiscoveryData = {
  customValuesKeys: {},
  totalRecordsAnalyzed: 0,
  executionTimeMs: 0,
  analysisStartTime: '',
  analysisEndTime: '',
  communityId: '',
};

/**
 * Lazy-query hook for custom values discovery.
 *
 * Returns a `getCustomValues(communityId, fetchPolicy?)` trigger function
 * so callers control exactly when the network request fires.
 *
 * @see apps/soteria-ui/src/content-search/hooks/useMetadataFilters.ts
 */
export function useCustomValuesDiscovery() {
  const [executeDiscovery, { data, loading, error }] = useLazyQuery<CustomValuesDiscoveryQueryResponse>(CUSTOM_VALUES_DISCOVERY, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const customValues = useMemo(() => data?.customValuesDiscovery || DEFAULT_CUSTOM_VALUES, [data]);

  const getCustomValues = useCallback(
    (communityId: string, fetchPolicy?: FetchPolicy) =>
      executeDiscovery({
        variables: {
          input: { communityId },
        },
        fetchPolicy: fetchPolicy || 'cache-and-network',
      }),
    [executeDiscovery]
  );

  return {
    customValues,
    loading,
    errors,
    getCustomValues,
  };
}
